import React, { useEffect, useState } from "react";
import api, { fmtINR, fmtDate } from "../lib/api";
import { Page, Field } from "../components/Primitives";
import { toast } from "sonner";
import { Plus, X, Trash2 } from "lucide-react";

const emptyVendor = { name: "", phone: "", email: "", gstin: "", address: "", category: "" };
const emptyPOLine = { item_id: "", qty: 1, rate: 0, gst_rate: 18 };

export default function Vendors() {
  const [tab, setTab] = useState("vendors");
  const [vendors, setVendors] = useState([]);
  const [pos, setPos] = useState([]);
  const [items, setItems] = useState([]);
  const [openV, setOpenV] = useState(false);
  const [openPO, setOpenPO] = useState(false);
  const [formV, setFormV] = useState(emptyVendor);
  const [formPO, setFormPO] = useState({ vendor_id: "", lines: [{ ...emptyPOLine }], notes: "", expected_date: "" });

  const load = async () => {
    const [v, p, i] = await Promise.all([api.get("/vendors"), api.get("/purchase-orders"), api.get("/stores/items")]);
    setVendors(v.data); setPos(p.data); setItems(i.data);
  };
  useEffect(() => { load(); }, []);

  const saveV = async (e) => {
    e.preventDefault();
    try { await api.post("/vendors", formV); toast.success("Added"); setOpenV(false); setFormV(emptyVendor); load(); } catch { toast.error("Failed"); }
  };
  const savePO = async (e) => {
    e.preventDefault();
    try {
      await api.post("/purchase-orders", {
        ...formPO, lines: formPO.lines.map((l) => ({ ...l, qty: +l.qty, rate: +l.rate, gst_rate: +l.gst_rate })),
      });
      toast.success("PO created"); setOpenPO(false);
      setFormPO({ vendor_id: "", lines: [{ ...emptyPOLine }], notes: "", expected_date: "" }); load();
    } catch { toast.error("Failed"); }
  };
  const receivePO = async (po) => {
    try {
      await api.post("/grn", {
        po_id: po.id,
        lines: po.lines.map((l) => ({ item_id: l.item_id, received_qty: l.qty })),
      });
      toast.success("GRN recorded · stock increased"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const updV = (k) => (e) => setFormV({ ...formV, [k]: e.target.value });
  const updPO = (k) => (e) => setFormPO({ ...formPO, [k]: e.target.value });
  const setLn = (i, patch) => {
    const ls = [...formPO.lines]; ls[i] = { ...ls[i], ...patch };
    if (patch.item_id) {
      const it = items.find((x) => x.id === patch.item_id);
      if (it) { ls[i].rate = it.cost_price; ls[i].gst_rate = it.gst_rate; }
    }
    setFormPO({ ...formPO, lines: ls });
  };
  const addLn = () => setFormPO({ ...formPO, lines: [...formPO.lines, { ...emptyPOLine }] });
  const delLn = (i) => setFormPO({ ...formPO, lines: formPO.lines.filter((_, j) => j !== i) });

  return (
    <Page number="08" title="Vendors & Procurement" subtitle="Suppliers master, purchase orders, goods receipt (GRN).">
      <div className="kk-tabs mb-5">
        <button onClick={() => setTab("vendors")} className={"kk-tab " + (tab === "vendors" ? "kk-tab-active" : "")} data-testid="tab-vendors">Vendors</button>
        <button onClick={() => setTab("pos")} className={"kk-tab " + (tab === "pos" ? "kk-tab-active" : "")} data-testid="tab-pos">Purchase Orders</button>
      </div>

      {tab === "vendors" && (
        <>
          <div className="flex justify-end mb-3">
            <button className="kk-btn-primary" onClick={() => setOpenV(true)} data-testid="add-vendor"><Plus size={14}/> Add Vendor</button>
          </div>
          <table className="kk-table">
            <thead><tr><th>Name</th><th>Category</th><th>Phone</th><th>GSTIN</th></tr></thead>
            <tbody>
              {vendors.map((v) => (
                <tr key={v.id}><td>{v.name}</td><td>{v.category}</td><td>{v.phone}</td><td className="font-mono">{v.gstin}</td></tr>
              ))}
              {vendors.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-[var(--kk-muted)]">No vendors.</td></tr>}
            </tbody>
          </table>
        </>
      )}

      {tab === "pos" && (
        <>
          <div className="flex justify-end mb-3">
            <button className="kk-btn-primary" onClick={() => setOpenPO(true)} data-testid="add-po"><Plus size={14}/> New PO</button>
          </div>
          <table className="kk-table">
            <thead><tr><th>#</th><th>Vendor</th><th>Total</th><th>Status</th><th>Expected</th><th></th></tr></thead>
            <tbody>
              {pos.map((p) => {
                const vendor = vendors.find((v) => v.id === p.vendor_id) || {};
                return (
                  <tr key={p.id}>
                    <td className="font-mono">{p.code}</td>
                    <td>{vendor.name}</td>
                    <td className="font-mono">{fmtINR(p.total)}</td>
                    <td><span className="kk-pill kk-pill-muted">{p.status}</span></td>
                    <td className="font-mono text-xs">{p.expected_date ? fmtDate(p.expected_date) : "—"}</td>
                    <td>{p.status !== "received" && <button className="kk-pill kk-pill-lime" onClick={() => receivePO(p)} data-testid={`grn-${p.id}`}>Receive (GRN)</button>}</td>
                  </tr>
                );
              })}
              {pos.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-[var(--kk-muted)]">No POs.</td></tr>}
            </tbody>
          </table>
        </>
      )}

      {openV && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => setOpenV(false)}>
          <form onSubmit={saveV} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[var(--kk-surface)] p-8 overflow-y-auto">
            <div className="flex items-center justify-between"><h2 className="font-display text-2xl uppercase">Add Vendor</h2><button type="button" onClick={() => setOpenV(false)}><X/></button></div>
            <div className="grid grid-cols-2 gap-5 mt-5">
              <div className="col-span-2"><Field label="Name *"><input className="kk-input" required value={formV.name} onChange={updV("name")} /></Field></div>
              <Field label="Phone"><input className="kk-input" value={formV.phone} onChange={updV("phone")} /></Field>
              <Field label="Email"><input className="kk-input" type="email" value={formV.email} onChange={updV("email")} /></Field>
              <Field label="Category"><input className="kk-input" value={formV.category} onChange={updV("category")} /></Field>
              <Field label="GSTIN"><input className="kk-input" value={formV.gstin} onChange={updV("gstin")} /></Field>
              <div className="col-span-2"><Field label="Address"><textarea className="kk-input min-h-[60px]" value={formV.address} onChange={updV("address")} /></Field></div>
            </div>
            <button className="kk-btn-primary mt-6" type="submit">Save</button>
          </form>
        </div>
      )}

      {openPO && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => setOpenPO(false)}>
          <form onSubmit={savePO} onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-[var(--kk-surface)] p-8 overflow-y-auto">
            <div className="flex items-center justify-between"><h2 className="font-display text-2xl uppercase">New PO</h2><button type="button" onClick={() => setOpenPO(false)}><X/></button></div>
            <div className="grid grid-cols-2 gap-5 mt-5">
              <Field label="Vendor *">
                <select className="kk-input" required value={formPO.vendor_id} onChange={updPO("vendor_id")}>
                  <option value="">—</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </Field>
              <Field label="Expected date"><input className="kk-input" type="date" value={formPO.expected_date} onChange={updPO("expected_date")} /></Field>
              <div className="col-span-2"><Field label="Notes"><textarea className="kk-input min-h-[50px]" value={formPO.notes} onChange={updPO("notes")} /></Field></div>
            </div>
            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <div className="kk-overline">Lines</div>
                <button type="button" className="kk-pill kk-pill-black" onClick={addLn}><Plus size={12}/> Add</button>
              </div>
              <table className="kk-table">
                <thead><tr><th>Item</th><th>Qty</th><th>Rate</th><th>GST%</th><th></th></tr></thead>
                <tbody>
                  {formPO.lines.map((l, idx) => (
                    <tr key={idx}>
                      <td>
                        <select className="kk-input py-1 text-xs" value={l.item_id} onChange={(e) => setLn(idx, { item_id: e.target.value })}>
                          <option value="">—</option>
                          {items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                        </select>
                      </td>
                      <td><input className="kk-input py-1 text-sm w-16" type="number" value={l.qty} onChange={(e) => setLn(idx, { qty: e.target.value })} /></td>
                      <td><input className="kk-input py-1 text-sm w-24" type="number" value={l.rate} onChange={(e) => setLn(idx, { rate: e.target.value })} /></td>
                      <td><input className="kk-input py-1 text-sm w-16" type="number" value={l.gst_rate} onChange={(e) => setLn(idx, { gst_rate: e.target.value })} /></td>
                      <td><button type="button" onClick={() => delLn(idx)}><Trash2 size={14}/></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button className="kk-btn-primary mt-6" type="submit">Create PO</button>
          </form>
        </div>
      )}
    </Page>
  );
}
